/* eslint-disable */
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { Button } from '../../Components/ui/button'
import { Input } from '../../Components/ui/input'
import { Label } from '../../Components/ui/label'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../Components/ui/dialog'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../Components/ui/sheet'

import { CircleCheck } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { post } from '../../http/restservice'
import { useRecoilState, useRecoilValue } from 'recoil'
import { userDetailsState } from '../LoginState'
import { useEffect, useState } from 'react'
import useMediaQuery from '../../hooks/useMediaQuery'
import { Toaster } from '../../Components/ui/toaster'
import { useToast } from '../../Components/ui/use-toast'
import { getUserDetails } from '../../utils/AuthUtil'
import PostHogTracker from '../../utils/PostHogTracker'
import { toCurrency } from '../../utils/CurrencyUtil'

export const TaxFiling = () => {
  const navigate = useNavigate()
  const [userDetails, setUserDetails] = useRecoilState(userDetailsState)
  const [selectedPlan, setSelectedPlan] = useState({})
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const { toast } = useToast()

  useEffect(() => {
    // Track tax filing page viewed
    PostHogTracker.pageViewed('Tax Filing')
    setUserDetails(getUserDetails())
  }, [])
  const services = [
    { id: 1, title: 'Personalised tax review & suggestions' },
    { id: 2, title: 'Dedicated expert to assist you' },
    { id: 3, title: 'Optimisations to reduce tax liability' },
    { id: 4, title: 'Post-filing support & reports' },
  ]

  const plans = [
    {
      id: 1,
      level: 'Basic',
      for: isDesktop
        ? 'For income via salary < 20 LPA, interest, or rent.'
        : 'For professionals with income lesser than 20 LPA',
      props: [
        'Salaried professionals',
        'Non-salaried professionals',
        '2 or lesser income streams',
        'Rental or interest income',
      ],
      price: 2599,
      discount: 40,
      actualPrice: 4599,
      icon: '/Tax/basic.svg',
      href: 'https://wa.me/+918050034800?text=I%20want%20to%20know%20more%20about%20the%20Basic%20ITR%20filing%20service%20on%20Fabits',
      svg: '/Tax/basic_circle.svg',
      isAdvance: true,
    },
    {
      id: 2,
      level: 'Advanced',
      for: isDesktop
        ? 'For income via salary < 50 LPA, interest, or rent.'
        : 'For professionals with income lesser than 50 LPA',
      props: [
        'Capital gains/losses from MFs, stocks, property, lottery, etc',
        'Long-term investors',
        'Traders',
        'More than 2 income streams',
      ],
      price: 5999,
      discount: 60,
      actualPrice: 9999,
      icon: '/Tax/advanced.svg',
      href: 'https://wa.me/+918050034800?text=I%20want%20to%20know%20more%20about%20the%20Advanced%20ITR%20filing%20service%20on%20Fabits',
      svg: '/Tax/advanced_circle.svg',
      isAdvance: false,
    },
    {
      id: 3,
      level: 'Pro',
      for: 'For salary above 50 LPA, ESOPs/RSUs, plus all in Advanced',
      props: [
        'For income above 50 LPA',
        'For ESOPs/RSU holders',
        'For directors, and CXOs',
        'PSU employees',
      ],
      price: 9999,
      discount: 60,
      actualPrice: 16599,
      icon: '/Tax/pro.svg',
      href: 'https://wa.me/+918050034800?text=I%20want%20to%20know%20more%20about%20the%20Pro%20ITR%20filing%20service%20on%20Fabits',
      svg: '/Tax/pro_circle.svg',
      isAdvance: false,
    },
  ]

  const formik = useFormik({
    initialValues: {
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      phoneNumber: userDetails.phoneNumber?.replace(/^\+91/, ''),
      email: '',
      terms: true,
    },
    enableReinitialize: true,

    validationSchema: Yup.object({
      firstName: Yup.string()
        .matches(/^[A-Za-z\s]+$/, 'First name can only contain alphabets')
        .required('First name is required'),
      lastName: Yup.string()
        .matches(/^[A-Za-z\s]+$/, 'Last name can only contain alphabets')
        .required('Last name is required'),
      phoneNumber: Yup.string()
        .matches(/^[0-9]{10}$/, 'Phone number must be exactly 10 digits')
        .required('Phone number is required'),
      email: Yup.string().email('Invalid email address').required('Email is required'),
      terms: Yup.boolean().oneOf([true], '').default(true),
    }),

    onSubmit: async (values, { setSubmitting }) => {
      // Track tax filing submission
      PostHogTracker.taxFilingSubmitted(selectedPlan.level)

      PostHogTracker.updateUserProfile({
        Site: {
          Name: values.firstName + ' ' + values.lastName,
          Email: values.email,
          Phone: values.phoneNumber,
          'MSG-email': true, // Disable email notifications
          'MSG-push': true, // Enable push notifications
          'MSG-sms': true, // Enable sms notifications
          'MSG-whatsapp': true, // Enable whatsapp notifications
        },
      })

      const payload = {
        firstName: values.firstName,
        lastName: values.lastName,
        phoneNumber: '+91' + values.phoneNumber,
        email: values.email,
        planSelected: selectedPlan.level,
        planAmount: selectedPlan.price,
      }

      const response = await post(`/customerservice/api/taxFiling`, payload)

      if (response.isError) {
        setSubmitting(false)

        // Track tax filing failure
        PostHogTracker.taxFilingFailed(
          selectedPlan.level,
          response?.response?.message || 'Submission failed',
        )

        toast({
          variant: 'destructive',
          title: 'Uh-oh',
          description: 'Something went wrong. Try again later.',
        })
      } else {
        setSubmitting(false)

        // Track tax filing success
        PostHogTracker.taxFilingSuccess(selectedPlan.level)

        toast({
          variant: 'success',
          title: 'Good to go!',
          description: 'We will contact you soon regarding your tax filing needs',
        })

        isDesktop
          ? document.getElementById('closeDialogButton').click()
          : document.getElementById('closeSheet').click()
      }
    },
  })

  return (
    <>
      <div className='fabits-page relative w-full pb-28 font-IBM md:pb-0'>
        <div className='relative px-4 py-12 text-center md:px-[192px] md:py-[70px]'>
          <div
            className='absolute top-[-72px] inset-0 mix-blend-multiply opacity-75 -z-10'
            style={{
              background: 'url(/Tax/Herobg.svg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          ></div>

          <h1 className='text-[32px] font-semibold leading-[38px] tracking-normal text-black-950 md:text-[3.4rem] md:leading-[64px]'>
            <span className='text-primary-600'>Filing taxes</span>
            <br />
            <span>shouldn’t be taxing</span>
          </h1>
          <p className='mt-3 text-sm leading-5 text-black-500 md:text-base'>
            Grab a coffee, and chill! <br className='md:hidden ml-1' />
            Let our experts do the hard work.
          </p>
          <div className='mx-auto mt-8 flex items-center justify-center gap-x-3 md:mt-[50px]'>
            <button
              className='w-full rounded-full border border-black-200 bg-white py-3 font-semibold text-black-950 lg:w-auto lg:px-[48px] lg:py-[10px]'
              variant='outline'
              onClick={() =>
                window.open(
                  'https://wa.me/+918050034800?text=I%20want%20to%20know%20more%20about%20the%20ITR%20filing%20service%20on%20Fabits.',
                )
              }
            >
              Talk to us
            </button>
            <a href='#plans' className='w-full lg:w-auto'>
              <button className='w-full rounded-full bg-primary-600 py-3 font-semibold text-white lg:w-auto lg:px-[48px] lg:py-[10px]'>
                Explore Plans
              </button>
            </a>
          </div>
        </div>

        <div className='flex items-center p-4 md:hidden'>
          <div className='md:text-center'>
            <h2 className='text-xl font-semibold leading-7 text-black-950'>Services we offer</h2>
            <p className='text-sm leading-5 text-black-400'>
              Get a dedicated expert to review & file ITR for you{' '}
            </p>

            <ul className='flex flex-col gap-y-[8px] mt-4'>
              {services.map((service) => (
                <li
                  key={service.id}
                  className='flex items-start justify-start gap-x-2 text-sm leading-5 text-black-700'
                >
                  <CircleCheck className='mt-[2px] h-4 w-4 flex-shrink-0 text-secondary-600' />
                  {service.title}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className='md:px-[6vw] lg:px-[12.7vw] md:py-[72px]'>
          <div className='mx-3 my-5 md:m-5 md:text-center'>
            <h3 className='text-xl font-semibold leading-7 text-black-950 md:text-[2rem] md:leading-[2.625rem]'>
              Plans for everyone
            </h3>
            <p className='text-sm leading-5 text-black-400 md:text-base'>
              Reduce liability, maximise income!
            </p>
          </div>
          <div
            className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[1.5rem] p-4 lg:p-0'
            id='plans'
          >
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`${
                  plan.level === 'Basic'
                    ? 'border border-[#C6D2F5]'
                    : plan.level === 'Advanced'
                    ? 'border border-[#C6D2F5]'
                    : 'border border-[#E8F37A]'
                } flex flex-1 flex-col justify-between rounded-lg bg-white pb-2 shadow-[0_8px_24px_rgba(4,3,19,0.05)]`}
                style={{
                  background:
                    plan.level === 'Basic'
                      ? 'radial-gradient(77.04% 68.72% at 16.46% 11.89%, rgba(40, 24, 255, 0.08) 0%, rgba(40, 24, 255, 0.00) 100%)'
                      : plan.level === 'Advanced'
                      ? 'radial-gradient(77.04% 68.72% at 16.46% 11.89%, rgba(40, 24, 255, 0.08) 0%, rgba(40, 24, 255, 0.00) 100%)'
                      : 'radial-gradient(77.04% 68.72% at 16.46% 11.89%, rgba(156, 211, 11, 0.14) 0%, rgba(156, 211, 11, 0.00) 100%)',
                }}
              >
                <div className='flex items-start justify-start px-2'>
                  <img src={plan.icon} alt={plan.level} />
                  <div>
                    <h3
                      className={`${
                        plan.level === 'Basic'
                          ? 'text-primary-600'
                          : plan.level === 'Advanced'
                          ? 'text-primary-600'
                          : 'text-black-950'
                      } mt-5 text-xl font-semibold`}
                    >
                      {plan.level}
                    </h3>
                    <p className='text-sm leading-5 text-black-500 md:text-base md:font-medium'>
                      {plan.for}
                    </p>
                  </div>
                </div>
                <div className='px-4 py-3'>
                  <p
                    className={`${
                      plan.level === 'Basic'
                        ? 'text-primary-600'
                        : plan.level === 'Advanced'
                        ? 'text-primary-600'
                        : 'text-black-950'
                    } text-base font-semibold md:text-lg`}
                  >
                    This plan is perfect for
                  </p>
                  <ul className='my-2 flex flex-col gap-y-2 py-2'>
                    {plan.props.map((prop, i) => (
                      <li
                        key={prop + i}
                        className='flex items-start justify-start gap-x-2 text-sm font-medium leading-5 text-black-700'
                      >
                        <CircleCheck className='mt-[2px] h-4 w-4 flex-shrink-0 text-secondary-600' />
                        {prop}
                      </li>
                    ))}
                  </ul>

                  <div className='mt-5 flex items-end gap-x-3'>
                    <h1
                      className={`${
                        plan.level === 'Basic'
                          ? 'text-primary-600'
                          : plan.level === 'Advanced'
                          ? 'text-primary-600'
                          : 'text-black-950'
                      } custom-font text-3xl font-semibold leading-10 md:text-4xl md:leading-[3rem]`}
                    >
                      &#8377;
                      {toCurrency(plan.price)}
                    </h1>
                    <span
                      className={`${
                        plan.level === 'Basic'
                          ? 'text-black-500'
                          : plan.level === 'Advanced'
                          ? 'text-black-500'
                          : 'text-black-500'
                      } relative custom-font`}
                    >
                      &#8377; {toCurrency(plan.actualPrice)}
                      <img
                        className='absolute top-3 left-1/2 transform -translate-x-1/2 -translate-y-1/2'
                        src='/Tax/cross.svg'
                      />
                    </span>
                    <span className='font-semibold text-error-600'>(-{plan.discount}%)</span>
                  </div>
                  <p className='text-xs leading-4 text-black-400'>
                    One time service fee, inclusive of GST
                  </p>

                  <p className='text-sm leading-5 text-black-400'>
                    In case of advance tax filing, the service fee is ₹ 2,000
                  </p>

                  {isDesktop ? (
                    <Dialog>
                      <DialogTrigger
                        className={`${
                          plan.level === 'Basic'
                            ? 'bg-primary-600'
                            : plan.level === 'Advanced'
                            ? 'bg-primary-600'
                            : 'bg-black-950'
                        } mt-5 hidden w-full rounded-full px-[24px] py-[10px] font-semibold text-white md:block`}
                        variant='outline'
                        onClick={() => {
                          PostHogTracker.taxPlanSelected(plan.level)
                          setSelectedPlan(plan)
                        }}
                      >
                        Select {plan.level} Plan
                      </DialogTrigger>
                      <DialogContent className='hidden md:block'>
                        <DialogHeader>
                          <DialogTitle className='text-start'>{plan.level}</DialogTitle>
                          <DialogDescription className='text-start'>{plan.for}</DialogDescription>
                        </DialogHeader>
                        <div className='border-b py-2' />

                        <form onSubmit={formik.handleSubmit}>
                          <div className='grid gap-4 py-4'>
                            <div className='flex gap-x-2'>
                              <div className='flex flex-col gap-y-2 justify-start items-start w-full'>
                                <Label htmlFor='firstName' className='text-start'>
                                  First name
                                </Label>
                                <Input
                                  id='firstName'
                                  name='firstName'
                                  type='text'
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.firstName}
                                  placeholder='eg. Dhruv'
                                />
                                {formik.touched.firstName && formik.errors.firstName ? (
                                  <div className='text-red-500'>{formik.errors.firstName}</div>
                                ) : null}
                              </div>
                              <div className='flex flex-col gap-y-2 justify-start items-start w-full'>
                                <Label htmlFor='lastName' className='text-start'>
                                  Last name
                                </Label>
                                <Input
                                  id='lastName'
                                  name='lastName'
                                  type='text'
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.lastName}
                                  placeholder='eg. Sharma'
                                />
                                {formik.touched.lastName && formik.errors.lastName ? (
                                  <div className='text-red-500'>{formik.errors.lastName}</div>
                                ) : null}
                              </div>
                            </div>

                            <div className='flex gap-x-2'>
                              <div className='flex flex-col gap-y-2 justify-start items-start w-full'>
                                <Label htmlFor='phoneNumber' className='text-left'>
                                  Phone number
                                </Label>
                                <Input
                                  id='phoneNumber'
                                  name='phoneNumber'
                                  type='text'
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.phoneNumber}
                                  placeholder='eg. 9788990099'
                                />
                                {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
                                  <div className='text-red-500'>{formik.errors.phoneNumber}</div>
                                ) : null}
                              </div>

                              <div className='flex flex-col gap-y-2 justify-start items-start w-full'>
                                <Label htmlFor='email' className='text-left'>
                                  Email address
                                </Label>
                                <Input
                                  id='email'
                                  name='email'
                                  type='email'
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.email}
                                  placeholder='eg. dhruv.sharma@gmail.com'
                                />
                                {formik.touched.email && formik.errors.email ? (
                                  <div className='text-red-500'>{formik.errors.email}</div>
                                ) : null}
                              </div>
                            </div>

                            <div className='flex items-center px-1 space-x-2'>
                              {/* <input
                                className='h-4 w-4'
                                type='checkbox'
                                id='terms'
                                name='terms'
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                checked={formik.values.terms}
                              />
                              <label
                                htmlFor='terms'
                                className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                              >
                                I wish to receive communication from Fabits
                              </label> */}
                              <div className='checkbox-wrapper-46'>
                                <input
                                  type='checkbox'
                                  name='terms'
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  checked={formik.values.terms}
                                  id='cbx-46'
                                  className='inp-cbx'
                                />
                                <label for='cbx-46' className='cbx'>
                                  <span>
                                    <svg viewBox='0 0 12 10' height='10px' width='12px'>
                                      <polyline points='1.5 6 4.5 9 10.5 1'></polyline>
                                    </svg>
                                  </span>
                                  <span className='text-sm font-medium'>
                                    I wish to receive communication from Fabits
                                  </span>
                                </label>
                              </div>
                              {formik.touched.terms && formik.errors.terms ? (
                                <div className='text-red-500'>{formik.errors.terms}</div>
                              ) : null}
                            </div>
                          </div>

                          <div className='border-b py-2' />

                          <div className='flex justify-between items-center w-full mt-3'>
                            <Button
                              className='mt-2'
                              variant='link'
                              onClick={() => window.open(plan.href)}
                            >
                              Get in touch
                            </Button>
                            <div className='w-full flex justify-end gap-x-5'>
                              <DialogClose asChild>
                                <Button
                                  variant='outline'
                                  className='py-[10px] px-[24px]'
                                  id='closeDialogButton'
                                >
                                  Close
                                </Button>
                              </DialogClose>
                              <Button
                                type='submit'
                                className='py-[10px] px-[24px]'
                                disabled={!formik.values.terms || formik.isSubmitting}
                              >
                                {formik.isSubmitting ? 'Submitting...' : 'Request callback'}
                              </Button>
                            </div>
                          </div>
                        </form>
                      </DialogContent>
                    </Dialog>
                  ) : (
                    <Sheet>
                      <SheetTrigger asChild>
                        <Button
                          className={`${
                            plan.level === 'Basic'
                              ? 'bg-primary-600'
                              : plan.level === 'Advanced'
                              ? 'bg-primary-600'
                              : 'bg-black-950'
                          } mt-5 block w-full rounded-full text-white lg:hidden`}
                          variant='outline'
                          onClick={() => setSelectedPlan(plan)}
                        >
                          Select {plan.level} Plan
                        </Button>
                      </SheetTrigger>
                      <SheetContent side='bottom' className='z-[1010] rounded-t-[20px] lg:hidden'>
                        <SheetHeader>
                          <SheetTitle>{plan.level}</SheetTitle>
                          <SheetDescription>{plan.for} </SheetDescription>
                        </SheetHeader>
                        <form onSubmit={formik.handleSubmit}>
                          <div className='grid gap-4 py-4'>
                            <div className='flex gap-x-2 justify-between items-start'>
                              <div className='w-full'>
                                <Label htmlFor='firstName' className='text-left'>
                                  First name
                                </Label>
                                <Input
                                  id='firstName'
                                  name='firstName'
                                  type='text'
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.firstName}
                                  placeholder='eg. Dhruv'
                                />
                                {formik.touched.firstName && formik.errors.firstName ? (
                                  <div className='text-red-500'>{formik.errors.firstName}</div>
                                ) : null}
                              </div>
                              <div className='w-full'>
                                <Label htmlFor='lastName' className='text-left'>
                                  Last name
                                </Label>
                                <Input
                                  id='lastName'
                                  name='lastName'
                                  type='text'
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  value={formik.values.lastName}
                                  placeholder='eg. Sharma'
                                />
                                {formik.touched.lastName && formik.errors.lastName ? (
                                  <div className='text-red-500'>{formik.errors.lastName}</div>
                                ) : null}
                              </div>
                            </div>

                            <div className='flex flex-col gap-y-2 justify-start items-start'>
                              <Label htmlFor='phoneNumber' className='text-left'>
                                Phone number
                              </Label>
                              <Input
                                id='phoneNumber'
                                name='phoneNumber'
                                type='text'
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.phoneNumber}
                                placeholder='eg. 9788990099'
                              />
                              {formik.touched.phoneNumber && formik.errors.phoneNumber ? (
                                <div className='text-red-500'>{formik.errors.phoneNumber}</div>
                              ) : null}
                            </div>

                            <div className='flex flex-col gap-y-2 justify-start items-start'>
                              <Label htmlFor='email' className='text-left'>
                                Email address
                              </Label>
                              <Input
                                id='email'
                                name='email'
                                type='email'
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.email}
                                placeholder='eg. dhruv.sharma@gmail.com'
                              />
                              {formik.touched.email && formik.errors.email ? (
                                <div className='text-red-500'>{formik.errors.email}</div>
                              ) : null}
                            </div>

                            <div className='flex items-center px-1 space-x-2'>
                              <input
                                className='h-4 w-4'
                                type='checkbox'
                                id='terms'
                                name='terms'
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                checked={formik.values.terms}
                              />
                              <label
                                htmlFor='terms'
                                className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'
                              >
                                I wish to receive communication from Fabits
                              </label>
                              {formik.touched.terms && formik.errors.terms ? (
                                <div className='text-red-500'>{formik.errors.terms}</div>
                              ) : null}
                            </div>
                          </div>
                          <SheetFooter>
                            <Button
                              type='submit'
                              disabled={!formik.values.terms || formik.isSubmitting}
                            >
                              {formik.isSubmitting ? 'Submitting...' : 'Request callback'}
                            </Button>
                          </SheetFooter>
                        </form>
                        <div className='flex justify-center items-center w-full'>
                          <Button
                            className='mt-2'
                            variant='link'
                            onClick={() => window.open(plan.href)}
                          >
                            Get in touch
                          </Button>
                        </div>
                      </SheetContent>
                    </Sheet>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className='hidden p-5 md:flex md:items-center md:justify-center md:p-10'>
          <div className='md:text-center'>
            <h2 className='text-2xl font-semibold text-black-950'>Services we offer</h2>
            <p className='text-black-400'>Get a dedicated expert to review & file ITR for you </p>

            <ul className='grid grid-cols-1 gap-2 md:grid-cols-2 my-5'>
              {services.map((service) => (
                <li key={service.id} className='flex gap-x-2 text-black-700'>
                  <CircleCheck className='mt-[2px] h-4 w-4 flex-shrink-0 text-secondary-600' />
                  {service.title}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          className='border-t border-black-100 px-5 py-12 text-center'
          style={{
            background:
              'radial-gradient(77.04% 68.72% at 16.46% 11.89%, rgba(156, 211, 11, 0.12) 0%, rgba(156, 211, 11, 0.00) 100%)',
          }}
        >
          <h2 className='text-2xl font-semibold text-black-950'>Confused which plan to pick?</h2>
          <p className='mt-1 text-sm leading-5 text-black-500'>
            Get in touch with us for
            <br />
            complete, dedicated assistance
          </p>
          <button
            className='mx-auto mt-6 w-[90%] cursor-pointer rounded-full bg-primary-600 px-[48px] py-[10px] font-semibold text-white md:w-[15%]'
            onClick={() =>
              window.open(
                'https://wa.me/+918050034800?text=I%20want%20to%20know%20more%20about%20the%20ITR%20filing%20service%20on%20Fabits.',
              )
            }
          >
            Talk to us
          </button>
        </div>
      </div>
      <Toaster />
    </>
  )
}
